import React, { useMemo, useRef, useState } from "react";

import { Button } from "../../../../components/ui/button";
import { Textarea } from "../../../../components/ui/textarea";
import { WORKSPACE_TOOL_PROJECTION_STATUSES } from "../../projectionContract";
import ToolProjectionNotice from "./ToolProjectionNotice";
import { mayRender, resolveToolRenderStatus } from "./toolProjectionGuard";

// Frozen at module scope so the fail-closed seed is one shared object
// rather than a new array on every render.
const EMPTY_NOTES = Object.freeze([]);

const SECTION_LABEL_CLASS = "text-[11px] font-semibold uppercase tracking-wide text-indigo-300";

// Same editable-field visual language established by Discovery/Quote
// (rounded-[8px] border-white/10 bg-white/5, indigo focus ring) — Notes'
// composer sits on the same Agent Toolbar glass surface those controls do.
const COMPOSER_CLASS =
  "min-h-[72px] w-full resize-none rounded-[8px] border border-white/10 bg-white/5 text-xs " +
  "text-slate-200 shadow-none placeholder:text-slate-600 focus-visible:outline-none " +
  "focus-visible:ring-1 focus-visible:ring-indigo-400 focus-visible:border-indigo-400/40";

// Absolute, non-relative timestamp format ("Aug 6, 2:05 PM") deliberately
// avoids "Today"/"Yesterday" labels, which would make rendering depend on
// the real wall-clock date and produce fragile, test-run-dependent output
// (WORKSPACE-AUDIT-006 SS instructs against exactly this). Purely a
// function of the note's own created_at -- no "now" reference needed.
function formatTimestamp(isoString) {
  const date = new Date(isoString);
  const datePart = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const timePart = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${datePart}, ${timePart}`;
}

// WORKSPACE-RUNTIME-001 §112: "Save uses a stable idempotency identity and
// remains pending until the owner confirms." This fake, provider-
// disconnected environment has no authoritative owner to ever perform that
// confirmation, so a newly-submitted note is rendered as pending and stays
// that way for the life of the mount -- it is never silently promoted to
// confirmed, which would misrepresent an owner action that never happened.
// Fixture-sourced notes (no `pending` flag) are already-confirmed history,
// per the audit's own classification -- only locally-submitted notes carry
// the flag.
function NotePendingBadge() {
  return (
    <span
      className="shrink-0 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-200 ring-1 ring-amber-400/30"
      data-testid="workspace-note-pending-badge"
    >
      Pending
    </span>
  );
}

/**
 * AW-NOTES-001 V1. A persisted note carries `author_user_id` and no display
 * name (`AgentNoteView`), so a name is rendered ONLY where the source
 * actually has one. The alternative was an empty author and a dangling
 * separator on every stored note.
 */
function authorLabel(note, currentUser) {
  if (note.created_by_name) return note.created_by_name;
  const authorId = note.author_user_id ?? note.created_by_user_id;
  if (authorId && currentUser?.id && authorId === currentUser.id) return "You";
  return null;
}

/**
 * AW-NOTES-001 V1. Four states, none of them a lie: nothing claimed while
 * idle, `Saving...` in flight, `Saved` ONLY after the server returned the
 * stored note, and `Not saved` with the text still in the composer.
 *
 * With no authorized call there is nothing to save to, and the reason is
 * shown rather than left as a dead button.
 */
function SaveStatus({ status, runId }) {
  if (!runId) {
    return (
      <p className="text-[11px] text-slate-500" data-testid="workspace-notes-no-run">
        No active call &mdash; notes can be saved once a call is connected.
      </p>
    );
  }
  if (status === "saving") {
    return (
      <p className="text-[11px] text-slate-400" data-testid="workspace-note-saving">
        Saving&hellip;
      </p>
    );
  }
  if (status === "saved") {
    return (
      <p className="text-[11px] text-emerald-400" data-testid="workspace-note-saved">
        Saved
      </p>
    );
  }
  if (status === "failed") {
    return (
      <p className="text-[11px] text-amber-500" data-testid="workspace-note-failed">
        Not saved &mdash; your text is still here. Press Save Note to try again.
      </p>
    );
  }
  return null;
}

function NoteRow({ note, currentUser = null }) {
  const author = authorLabel(note, currentUser);
  return (
    <div
      className="rounded-xl border border-white/10 bg-white/5 p-2.5"
      data-testid={`workspace-note-${note.id}`}
    >
      <p
        className="whitespace-pre-wrap break-words text-xs leading-relaxed text-slate-200"
        data-testid="workspace-note-body"
      >
        {note.body}
      </p>
      <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-500">
        <span data-testid="workspace-note-meta">
          {author ? `${author} · ` : ""}
          {formatTimestamp(note.created_at)}
        </span>
        {note.pending ? <NotePendingBadge /> : null}
      </p>
    </div>
  );
}

/**
 * Notes tool (WORKSPACE-AUDIT-006). A lightweight, append-only CRM note
 * feed: a composer plus a reverse-chronological list of prior notes.
 * Rehearses the approved product direction -- multiple author+timestamped
 * records, not the Script Runner Notes tab's one continuously-overwritten
 * browser-persisted string -- entirely in local React state. No backend
 * persistence, no browser storage, no contact_logs, no wiring to live
 * Lead/run data.
 *
 * These notes are conceptually scoped to "the lead currently loaded in
 * Agent Workspace," but this pass does not read a real lead id from any
 * runtime/API source -- there is no already-approved fixture seam for that
 * yet, so the fixture simply stands in for "this lead's notes" and the
 * prop contract below (`currentUser`/`notes`/`getNow`) is the seam a future
 * pass would thread a real lead id through.
 *
 * `getNow` is injectable so tests can supply a fixed reference time for new
 * notes' `created_at`; production/default behavior uses the real clock.
 *
 * WORKSPACE-AUDIT-007 §13 item 5(a): a locally-submitted note is marked
 * `pending: true` and rendered with a Pending badge (NotePendingBadge)
 * distinct from already-confirmed fixture notes, per WORKSPACE-RUNTIME-001
 * §112's "remains pending until the owner confirms." It is never silently
 * promoted to confirmed.
 */
export default function NotesPanel({
  currentUser,
  notes,
  getNow = () => new Date(),
  projectionStatus,
  /**
   * AW-NOTES-001 V1 persistence port. ABSENT by default, and when absent
   * this panel behaves exactly as it did before -- local-only, every note
   * permanently `pending` -- so no existing caller changes meaning. When
   * present, the server is the record: notes are loaded from it, Save is
   * explicit, and `Saved` appears only on acknowledgement.
   */
  notesData = null,
}) {
  // AW-ORIGINAL-RESTORE-001. Required, fail-closed. An EMPTY array of
  // notes is a legitimate authoritative answer and renders; an ABSENT
  // `notes` key never does.
  const renderStatus = resolveToolRenderStatus(projectionStatus, [currentUser, notes]);
  const renderable = mayRender(renderStatus);

  const [composerText, setComposerText] = useState("");
  const wired = Boolean(notesData);
  // Hooks run before the guard returns, so they are seeded with a safe
  // empty list when nothing may be rendered.
  const [localNotes, setLocalNotes] = useState(() => (renderable ? notes : EMPTY_NOTES));
  const noteIdRef = useRef(0);

  // Sorts a copy, never the source array/prop -- rendering must not depend
  // on (and must not corrupt) the order notes/localNotes happen to be in.
  // With the port present the SERVER's notes are authoritative; without it,
  // local state, exactly as before.
  const sourceNotes = Array.isArray(notesData?.notes) ? notesData.notes : localNotes;
  const sortedNotes = useMemo(
    () => [...sourceNotes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [sourceNotes]
  );

  if (!renderable) {
    return (
      <ToolProjectionNotice
        status={renderStatus.status}
        reason={renderStatus.reason}
        testIdPrefix="workspace-notes"
      />
    );
  }

  const composerValue = wired ? notesData.draft ?? "" : composerText;
  const setComposerValue = wired ? notesData.setDraft : setComposerText;
  const trimmedComposerText = composerValue.trim();
  const saveStatus = wired ? notesData.status : null;
  // Save is refused -- visibly -- when there is no authorized call to own
  // the note, rather than failing at the server.
  const saveDisabled = wired ? !notesData.canSave : !trimmedComposerText;

  const handleAddNote = () => {
    const body = composerValue.trim();
    if (!body) return;

    if (wired) {
      // The composer is NOT cleared here. It is cleared by the hook only on
      // acknowledgement, so a failed save leaves the agent's words exactly
      // where they are -- the whole of V1's recovery story.
      Promise.resolve(notesData.save());
      return;
    }

    noteIdRef.current += 1;
    const newNote = {
      // Stable per-submission idempotency identity (WORKSPACE-RUNTIME-001
      // §112) -- unique and unchanging for this submission, so a future
      // real save path could resolve retries of the same submission back
      // to this same identity instead of creating a duplicate.
      id: `local-note-${noteIdRef.current}`,
      body,
      created_at: getNow().toISOString(),
      created_by_user_id: currentUser.id,
      created_by_name: currentUser.name,
      // Remains pending until the owner confirms (WORKSPACE-RUNTIME-001
      // §112) -- never flipped to confirmed locally.
      pending: true,
    };

    setLocalNotes((previous) => [newNote, ...previous]);
    setComposerValue("");
  };

  return (
    <div className="flex flex-col gap-4" data-testid="workspace-notes-panel">
      <div className="flex flex-col gap-2">
        <Textarea
          value={composerValue}
          onChange={(event) => setComposerValue(event.target.value)}
          placeholder="Write a note..."
          className={COMPOSER_CLASS}
          data-testid="workspace-notes-composer"
        />
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            className="h-7 rounded-full px-3 text-[11px]"
            onClick={handleAddNote}
            disabled={saveDisabled}
            data-testid="workspace-notes-add-button"
          >
            {wired ? "Save Note" : "Add Note"}
          </Button>
        </div>
        {wired ? <SaveStatus status={saveStatus} runId={notesData.runId} /> : null}
      </div>

      {wired && notesData.loadError ? (
        <p className="text-xs text-amber-600" data-testid="workspace-notes-load-failed">
          Notes could not be loaded. They are not lost &mdash; this panel
          cannot show them right now.
        </p>
      ) : null}

      {wired && notesData.truncated ? (
        <p className="text-xs text-slate-400" data-testid="workspace-notes-truncated">
          Showing the most recent notes for this call. Older notes are stored
          but not listed here.
        </p>
      ) : null}

      <div className="flex flex-col gap-2">
        <p className={SECTION_LABEL_CLASS}>Recent Notes</p>
        {wired && notesData.loading && sortedNotes.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="workspace-notes-loading">
            Loading notes&hellip;
          </p>
        ) : sortedNotes.length === 0 && !(wired && notesData.loadError) ? (
          <p className="text-xs text-slate-500" data-testid="workspace-notes-empty">
            No notes yet.
          </p>
        ) : sortedNotes.length === 0 ? null : (
          <div className="flex flex-col gap-2" data-testid="workspace-notes-list">
            {sortedNotes.map((note) => (
              <NoteRow key={note.id} note={note} currentUser={currentUser} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
